import { useState } from 'react';

interface UseFormFieldsResult<T> {
    formFields: T;
    setFormField: (formField: string, fieldData: any) => void;
    clearFormFields: (fieldNames: Array<string>) => void;
}

function useFormFields<T>(initialValues: T): UseFormFieldsResult<T> {
    const defaultInputState = {
        errorMessage: null,
        isValid: null,
        value: '',
    };

    const initialForm: any = {};

    for (const [fieldName, value] of Object.entries(initialValues)) {
        switch (typeof value) {
            case 'object':
                initialForm[fieldName] = { ...defaultInputState, ...value };
                break;
            case 'string':
            default:
                initialForm[fieldName] = { ...defaultInputState, value };
        }
    }

    const [formFields, setForm] = useState(initialForm);

    const setFormField = (fieldName: string, fieldData: any) => {
        isFieldNameValid(fieldName);

        setForm((prevForm: any) => ({
            ...prevForm,
            [fieldName]: { ...prevForm[fieldName], ...fieldData },
        }));
    };

    const clearFormFields = (fieldNames: Array<string>) => {
        if (!Array.isArray(fieldNames)) {
            throw new Error('clearFormFields takes an array of fieldNames as input.');
        }

        fieldNames.forEach((fieldName) => setFormField(fieldName, defaultInputState));
    };

    const isFieldNameValid = (fieldName: string) => {
        if (!fieldName || typeof fieldName !== 'string') {
            throw new Error('A fieldName is required.');
        }

        if (!formFields[fieldName]) {
            throw new Error(`The fieldName ${fieldName} is not part of the form.`);
        }
    };

    return { clearFormFields, formFields, setFormField };
}

export default useFormFields;
